import React from 'react'

import { AppContext } from '@/components/base/appcontext'
import { ListLoader } from '@/components/base/listLoader'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * Per-screen placeholders, drawn in each screen's own layout (block heights
 * measured from the rendered screens) so the content lands where the
 * placeholders were. Kept out of the screens' chunks: the route shell shows
 * them while a screen's code and data are still on the way.
 */

function Status({ children }: { children: React.ReactNode }) {
    const appContext = React.useContext(AppContext)
    return (
        <div role="status" className="flex flex-col gap-3">
            <span className="sr-only">{appContext.geti18n('Loading...')}</span>
            {children}
        </div>
    )
}

/** A labelled block: a short caption line over a card of `height` px. */
function Section({
    height,
    className
}: {
    height: number
    className?: string
}) {
    return (
        <div className="flex flex-col gap-2">
            <Skeleton className="h-3.5 w-28 rounded-md" />
            <Skeleton
                className={cn('rounded-2xl', className)}
                style={{ height }}
            />
        </div>
    )
}

/** A form field: label line over an input of `height` px. */
function Field({ height = 40 }: { height?: number }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-24 rounded-md" />
            <Skeleton className="rounded-lg" style={{ height }} />
        </div>
    )
}

/** Where a screen's floating ActionBar sits. */
function ActionBarSlot() {
    return (
        <div
            aria-hidden="true"
            className="fixed bottom-2 left-4 right-4 h-12 rounded-2xl border border-white/10 bg-secondary/80"
        />
    )
}

/** A filter pill row over list rows (archived, transactions, requests). */
export function FilteredRowsSkeleton({ rows = 4 }: { rows?: number }) {
    return (
        <Status>
            <div className="flex gap-2">
                <Skeleton className="h-9 w-24 rounded-full" />
                <Skeleton className="h-9 w-24 rounded-full" />
            </div>
            <ListLoader rows={rows} />
        </Status>
    )
}

/** Programme editor: tab strip, the active tab's fields, the action bar. */
export function EditProgrammeSkeleton() {
    return (
        <Status>
            <Skeleton className="h-9 w-full rounded-lg" />
            <Field />
            <Field height={72} />
            <Field />
            <ListLoader rows={2} />
            <ActionBarSlot />
        </Status>
    )
}

/** Enrol: programme card, start date/time card, the schedule, action bar. */
export function StartProgrammeSkeleton() {
    return (
        <Status>
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-40 rounded-2xl" />
            {Array.from({ length: 4 }, (_, i) => (
                <Skeleton
                    key={i}
                    className="h-16 rounded-xl"
                    style={{ opacity: 1 - i * 0.18 }}
                />
            ))}
            <ActionBarSlot />
        </Status>
    )
}

/** Stop: the question line and its two buttons. */
export function StopProgrammeSkeleton() {
    return (
        <Status>
            <Skeleton className="m-4 h-6 w-4/5 rounded-md" />
            <div className="mx-4 flex gap-4">
                <Skeleton className="h-9 flex-1 rounded-md" />
                <Skeleton className="h-9 flex-1 rounded-md" />
            </div>
        </Status>
    )
}

/** Self-evaluation: intro, perception row, per-exercise rating rows. */
export function ReflectionSkeleton() {
    return (
        <Status>
            <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-full rounded-md" />
                <Skeleton className="h-4 w-11/12 rounded-md" />
                <Skeleton className="h-4 w-3/5 rounded-md" />
            </div>
            <div className="flex justify-center gap-4 py-2">
                {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton key={i} className="h-12 w-12 rounded-full" />
                ))}
            </div>
            {Array.from({ length: 4 }, (_, i) => (
                <div
                    key={i}
                    className="flex flex-col gap-2"
                    style={{ opacity: 1 - i * 0.18 }}
                >
                    <Skeleton className="h-4 w-1/3 rounded-md" />
                    <div className="flex gap-2">
                        {Array.from({ length: 5 }, (_, j) => (
                            <Skeleton
                                key={j}
                                className="h-9 w-9 rounded-full"
                            />
                        ))}
                    </div>
                </div>
            ))}
            <ActionBarSlot />
        </Status>
    )
}

/** The day view: week strip with progress, then the day's schedule. */
export function ScheduleDetailsSkeleton() {
    return (
        <Status>
            <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="grid flex-1 grid-cols-7 gap-1">
                    {Array.from({ length: 7 }, (_, i) => (
                        <div
                            key={i}
                            className="flex flex-col items-center gap-1"
                        >
                            <Skeleton className="h-3 w-3 rounded-sm" />
                            <Skeleton className="h-5 w-6 rounded-md" />
                        </div>
                    ))}
                </div>
                <Skeleton className="h-8 w-8 rounded-full" />
            </div>
            <Skeleton className="h-1 w-full rounded-full" />
            <Skeleton className="h-12 rounded-xl" />
            {Array.from({ length: 4 }, (_, i) => (
                <div
                    key={i}
                    className="flex items-center gap-3"
                    style={{ opacity: 1 - i * 0.18 }}
                >
                    <Skeleton className="h-4 w-12 rounded-md" />
                    <Skeleton className="h-14 flex-1 rounded-xl" />
                </div>
            ))}
        </Status>
    )
}

/** A request: status card, progress, counterpart and programme blocks. */
export function RequestDetailSkeleton() {
    return (
        <Status>
            <Skeleton className="h-44 rounded-2xl" />
            <Skeleton className="h-8 rounded-xl" />
            <Section height={88} className="rounded-xl" />
            <Section height={64} className="rounded-xl" />
        </Status>
    )
}

/** A conversation: alternating bubbles and the composer. */
export function MessagesSkeleton() {
    const sides = [
        'self-start w-2/3',
        'self-end w-1/2',
        'self-start w-3/5',
        'self-end w-2/5',
        'self-start w-1/2'
    ]
    return (
        <Status>
            <div className="flex flex-col gap-2">
                {sides.map((side, i) => (
                    <Skeleton
                        key={i}
                        className={cn('h-10 rounded-2xl', side)}
                    />
                ))}
            </div>
            <ActionBarSlot />
        </Status>
    )
}

/** Coach settings: toggles, price card, text fields, save button. */
export function CoachSettingsSkeleton() {
    return (
        <Status>
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-52 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-44 rounded-2xl" />
            <Skeleton className="h-12 rounded-xl" />
        </Status>
    )
}

/** Coach analytics and payouts: window pills, headline card, sections. */
export function CoachDashboardSkeleton() {
    return (
        <Status>
            <div className="flex gap-2">
                {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton key={i} className="h-9 w-20 rounded-full" />
                ))}
            </div>
            <Skeleton className="h-16 rounded-xl" />
            <Section height={108} />
            <Section height={148} className="rounded-xl" />
            <Section height={170} />
        </Status>
    )
}

/** Coach / business application: intro, fields, a long text field. */
export function ApplicationSkeleton() {
    return (
        <Status>
            <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-full rounded-md" />
                <Skeleton className="h-4 w-4/5 rounded-md" />
            </div>
            <Field />
            <Field />
            <Field />
            <Field height={66} />
            <Field height={140} />
            <Field />
        </Status>
    )
}

/** Workout request wizard: coach card, the first step's options. */
export function RequestWorkoutSkeleton() {
    return (
        <Status>
            <div className="flex items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-1/2 rounded-md" />
                    <Skeleton className="h-3 w-1/3 rounded-md" />
                </div>
            </div>
            <Skeleton className="h-6 w-2/3 rounded-md" />
            {Array.from({ length: 4 }, (_, i) => (
                <Skeleton
                    key={i}
                    className="h-14 rounded-xl"
                    style={{ opacity: 1 - i * 0.18 }}
                />
            ))}
            <Skeleton className="h-10 rounded-lg" />
        </Status>
    )
}

/** Leaderboard: audience tabs, period pills, ranked rows. */
export function LeaderboardSkeleton() {
    return (
        <Status>
            <Skeleton className="h-9 w-full rounded-lg" />
            <div className="flex gap-2">
                {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton key={i} className="h-8 w-20 rounded-full" />
                ))}
            </div>
            <ListLoader rows={6} />
        </Status>
    )
}

/** Invite: the QR card, the code and the share buttons. */
export function InviteSkeleton() {
    return (
        <Status>
            <Skeleton className="mx-auto h-56 w-56 rounded-2xl" />
            <Skeleton className="mx-auto h-5 w-40 rounded-md" />
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
        </Status>
    )
}

/** Own profile: header, segmented control, the account cards. */
export function UserProfileSkeleton() {
    return (
        <Status>
            <div className="flex items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-1/2 rounded-md" />
                    <Skeleton className="h-3 w-2/3 rounded-md" />
                </div>
            </div>
            <Skeleton className="h-10 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
        </Status>
    )
}

/** Plain text pages (articles, notices). */
export function TextSkeleton() {
    return (
        <Status>
            <Skeleton className="h-6 w-2/3 rounded-md" />
            {Array.from({ length: 8 }, (_, i) => (
                <Skeleton
                    key={i}
                    className="h-4 rounded-md"
                    style={{
                        width: `${[100, 95, 88, 100, 70, 92, 85, 60][i]}%`
                    }}
                />
            ))}
        </Status>
    )
}
